import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { formatEther } from "ethers";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

function StatisticsPage({ contract }) {
  const [topics, setTopics] = useState([]);
  const [totalTopics, setTotalTopics] = useState(0);

  useEffect(() => {
    const init = async () => {
      if (contract) {
        loadTopics(contract);

        contract.on("TopicCreated", (topicId, creator) => {
          console.log("TopicCreated event received:", { topicId, creator });
          handleNewTopic(contract, topicId.toString(), creator);
        });

        contract.on("BetPlaced", (topicId, user, amount, optionIndex) => {
          console.log("BetPlaced event received:", { topicId, user, amount, optionIndex });
          handleBetPlaced(contract, topicId, amount, optionIndex);
        });

        contract.on("TopicExecuted", (topicId) => {
          console.log("TopicExecuted event received:", { topicId });
          handleTopicExecuted(topicId);
        });

        contract.on("TopicCanceled", (topicId) => {
          console.log("TopicCanceled event received:", { topicId });
          handleTopicCanceled(topicId); // Add handler for canceled topics
        });

        // Start the interval to check for status updates
        const interval = setInterval(() => {
          checkAndUpdateStatus();
        }, 2000); // Check every 2 seconds

        return () => clearInterval(interval); // Clean up the interval on component unmount
      } else {
        // console.error("Contract instance not found!");
      }
    };

    init();

    return () => {
      if (contract) {
        contract.off("TopicCreated");
        contract.off("BetPlaced");
        contract.off("TopicExecuted");
        contract.off("TopicCanceled");
      }
    };
  }, [contract]);
  const checkAndUpdateStatus = () => {
    const currentTime = Math.floor(Date.now() / 1000);
    setTopics((prevTopics) => prevTopics.map((topic) => {
      const startDatePassed = currentTime > Number(topic.startDate);
      const endDatePassed = currentTime > Number(topic.endDate);

      // Only update if there is a change in status
      if (startDatePassed !== topic.startDatePassed || endDatePassed !== topic.endDatePassed) {
        return {
          ...topic,
          startDatePassed: startDatePassed,
          endDatePassed: endDatePassed,
        };
      }
      return topic;
    }));
  };

  const loadTopics = async (contractInstance) => {
    const topicsCount = await contractInstance.getTopicCounter();
    setTotalTopics(Number(topicsCount));
    let topics = [];
    const currentTime = Math.floor(Date.now() / 1000);
    // console.log("currentTime1:",currentTime);
    for (let i = 0; i < topicsCount; i++) {
      const topic = await contractInstance.getTopic(i);
      const formattedTopic = await formatTopic(contractInstance, topic, currentTime);
      topics.push(formattedTopic);
    }
    setTopics(topics.reverse());
  };

  const formatTopic = async (contractInstance, topic, currentTime) => {
    const startDatePassed = currentTime > BigInt(topic.timing.startDate);
    const endDatePassed = currentTime > BigInt(topic.timing.endDate);

    const options = await contractInstance.getOptions(topic.id);
    const participants = await contractInstance.getParticipants(topic.id);

    const betsPerOption = await Promise.all(options.map(async (option, index) => {
      const optionBets = await contractInstance.getBets(topic.id, index);
      return {
        option,
        betAmount: parseFloat(formatEther(optionBets)),  // Convert to number
        betAmountWei: BigInt(optionBets.toString())      // Store as BigInt for calculations
      };
    }));

    const totalBets = betsPerOption.reduce((total, option) => total + option.betAmountWei, 0n);
    const numberOfBets = participants.length;

    const bonusCoins = BigInt(topic.betting.bonusCoins.toString());
    const totalAmount = totalBets + bonusCoins;

    return {
      id: topic.id.toString(),
      question: topic.question,
      options: betsPerOption,
      totalBets: formatEther(totalBets.toString()),  // Convert to string for display
      bonusCoins: formatEther(bonusCoins.toString()),  // Convert to string for display
      totalAmount: formatEther(totalAmount.toString()),  // Convert to string for display
      minBetAmount: formatEther(topic.betting.minBetAmount.toString()), // Add minBetAmount
      maxBetAmount: formatEther(topic.betting.maxBetAmount.toString()), // Add maxBetAmount
      startDate: Number(topic.timing.startDate),
      endDate: Number(topic.timing.endDate),
      startDatePassed: startDatePassed,
      endDatePassed: endDatePassed,
      creator: topic.creator,
      isExecuted: topic.isExecuted,
      isCanceled: topic.isCanceled,
      numberOfBets: numberOfBets,
    };
  };



  const handleNewTopic = async (contractInstance, topicId, creator) => {
    const topic = await contractInstance.getTopic(topicId);
    const currentTime = Math.floor(Date.now() / 1000);
    const formattedTopic = await formatTopic(contractInstance, topic, currentTime);

    setTopics((prevTopics) => {
      if (!prevTopics.some(t => t.id === topicId.toString())) {
        return [formattedTopic, ...prevTopics];
      }
      return prevTopics;
    });

    setTotalTopics(prevTotal => prevTotal + 1);
  };
  const handleTopicCanceled = async (topicId) => {
    setTopics((prevTopics) => prevTopics.map((topic) => {
      if (topic.id === topicId.toString()) {
        return {
          ...topic,
          isCanceled: true,
        };
      }
      return topic;
    }));
  };

  const handleBetPlaced = async (contractInstance, topicId, amount, optionIndex) => {
    const updatedTopic = await contractInstance.getTopic(topicId);
    const currentTime = Math.floor(Date.now() / 1000);
    const formattedTopic = await formatTopic(contractInstance, updatedTopic, currentTime);

    setTopics((prevTopics) => prevTopics.map((topic) => {
      if (topic.id === topicId.toString()) {
        return formattedTopic;
      }
      return topic;
    }));
  };
  const handleTopicExecuted = async (topicId) => {
    setTopics((prevTopics) => prevTopics.map((topic) => {
      if (topic.id === topicId.toString()) {
        return {
          ...topic,
          isExecuted: true,
        };
      }
      return topic;
    }));
  };

  return (
    <div className="container mt-5">
      <Helmet>
        <title>6Degree - View Real-time Blockchain Statistics and Analytics</title>
        <meta name="description" content="Get insights and analytics on your 6Degree activities. Track performance, participation, and outcomes in real-time." />
      </Helmet>
      <h1>Statistics</h1>
      <p>Number of Topics: {totalTopics}</p>

      {topics.map((topic) => (
        <div key={topic.id} className="card mb-3">
          <div className="row no-gutters">
            <div className="col-md-6">
              <div className="card-body">
                <h5 className="card-title">Topic #{topic.id}: {topic.question}</h5>
                <ul>
                  <li>Start Date: {new Date(topic.startDate * 1000).toLocaleString()}</li>
                  <li>End Date: {new Date(topic.endDate * 1000).toLocaleString()}</li>
                </ul>
                <p>Status:&nbsp;
                  {topic.isCanceled ? (
                    <span className="badge bg-secondary">Cancelled <span className="dot"></span></span>
                  ) : !topic.startDatePassed ? (
                    <span className="badge bg-danger">Not Started <span className="spinner-grow spinner-grow-sm" role="status"></span></span>
                  ) : !topic.endDatePassed ? (
                    <span className="badge bg-success">Ongoing <span className="spinner-grow spinner-grow-sm" role="status"></span></span>
                  ) : topic.isExecuted ? (
                    <span className="badge bg-primary">Finalized <span className="dot"></span></span>
                  ) : (
                    <span className="badge bg-warning">Awaiting Finalization <span className="dot"></span></span>
                  )}
                </p>
                <ul>
                  <li>Total Bets: {topic.totalBets} 6DO</li>
                  <li>Total Bonus Coins: {topic.bonusCoins} 6DO</li>
                </ul>
                <h6>Total Amount: {topic.totalAmount} 6DO</h6>
                <ul>
                  <li>Min Bet Amount: {topic.minBetAmount} 6DO</li>
                  <li>Max Bet Amount: {topic.maxBetAmount} 6DO</li>
                </ul>
                <h6>Number of Participants: {topic.numberOfBets}</h6>

                <h6>Options:</h6>
                <ul>
                  {topic.options.map((option, i) => (
                    <li key={i}>
                      {option.option}: {option.betAmount} 6DO
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-md-6 d-flex align-items-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={topic.options}
                    dataKey="betAmount"  // betAmount should be a number
                    nameKey="option"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    label={({ name, value }) => `${name}: ${value} 6DO`}
                  >
                    {topic.options.map((entry, index) => {
                      const colors = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#8dd1e1"];
                      return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                    })}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

          </div>
        </div>
      ))}
    </div>
  );
}

export default StatisticsPage;